import { z } from "zod";
import {
  availableChatProvidersValidator,
  azureChatProviderValidator,
  junetChatProviderValidator,
  ollamaChatProviderValidator,
  openRouterChatProviderValidator,
  vllmChatProviderValidator,
  openAIChatProviderValidator,
  anthropicChatProviderValidator,
} from "./providers/chat-provider-validator";
import {
  availableEmbeddingProvidersValidator,
  azureEmbeddingProviderValidator,
  junetEmbeddingProviderValidator,
  ollamaEmbeddingProviderValidator,
} from "./providers/embedding-provider-validator";
import {
  availableVectorStoreProvidersValidator,
  azureVectorStoreProviderValidator,
  milvusVectorStoreProviderValidator,
} from "./providers/vector-store-provider-validator";
import {
  availableAuthProvidersValidator,
  microsoftAuthProviderValidator,
  googleAuthProviderValidator,
} from "./providers/auth-provider-validator";
import { systemNotificationEvents } from "../../types/system-notifications";

// Custom URL validator that allows empty strings and relative URLs
const optionalUrl = z
  .string()
  .optional()
  .refine(
    (val) => {
      if (!val || val === "") return true;
      // Allow relative URLs starting with /
      if (val.startsWith("/")) return true;
      // Allow full URLs
      return z.url().safeParse(val).success;
    },
    { message: "Must be a valid URL, relative path, or empty" }
  )
  .transform((val) => (val === "" ? undefined : val));

// Chat Settings
export const chatSettingsSchema = z
  .object({})
  .merge(availableChatProvidersValidator)
  .merge(azureChatProviderValidator)
  .merge(ollamaChatProviderValidator)
  .merge(vllmChatProviderValidator)
  .merge(junetChatProviderValidator)
  .merge(openRouterChatProviderValidator)
  .merge(openAIChatProviderValidator)
  .merge(anthropicChatProviderValidator);

// Embedding Settings
export const embeddingSettingsSchema = z
  .object({})
  .merge(availableEmbeddingProvidersValidator)
  .merge(azureEmbeddingProviderValidator)
  .merge(ollamaEmbeddingProviderValidator)
  .merge(junetEmbeddingProviderValidator);

// Vector Store Settings
export const vectorStoreSettingsSchema = z
  .object({})
  .merge(availableVectorStoreProvidersValidator)
  .merge(azureVectorStoreProviderValidator)
  .merge(milvusVectorStoreProviderValidator);

// Branding / Theme Settings
export const brandingSettingsSchema = z
  .object({
    app_title: z.string().optional(),
    logo_light_url: optionalUrl,
    logo_dark_url: optionalUrl,
    sidebar_logo_url: optionalUrl,
    sidebar_logo_enabled: z.boolean().optional(),
    sidebar_logo_size: z.number().min(20).max(100).optional(),
    favicon_url: optionalUrl,
    // Text content
    motd: z.string().optional(),
    pro_tip: z.string().optional(),
    motd_enabled: z.boolean().optional(),
    pro_tip_enabled: z.boolean().optional(),
    meta_theme_color: z.string().optional(),
    dark_meta_theme_color: z.string().optional(),
    // SSL Certificate settings
    ssl_enabled: z.boolean().optional(),
    ssl_certificate: z.string().optional(),
    ssl_certificate_key: z.string().optional(),
    ssl_domain: z.string().optional(),
    // Fonts
    font_sans: z.string().optional(),
    font_serif: z.string().optional(),
    font_mono: z.string().optional(),
    // CSS variable values (can be HSL like "246 72% 65%" or any CSS color)
    card: z.string().optional(),
    card_foreground: z.string().optional(),
    popover: z.string().optional(),
    popover_foreground: z.string().optional(),
    muted: z.string().optional(),
    muted_foreground: z.string().optional(),
    destructive: z.string().optional(),
    destructive_foreground: z.string().optional(),
    primary: z.string().optional(),
    primary_foreground: z.string().optional(),
    secondary: z.string().optional(),
    secondary_foreground: z.string().optional(),
    accent: z.string().optional(),
    accent_foreground: z.string().optional(),
    background: z.string().optional(),
    foreground: z.string().optional(),
    border: z.string().optional(),
    ring: z.string().optional(),
    input: z.string().optional(),
    chart_1: z.string().optional(),
    chart_2: z.string().optional(),
    chart_3: z.string().optional(),
    chart_4: z.string().optional(),
    chart_5: z.string().optional(),
    radius: z.string().optional(),
    shadow_2xs: z.string().optional(),
    shadow_xs: z.string().optional(),
    shadow_sm: z.string().optional(),
    shadow: z.string().optional(),
    shadow_md: z.string().optional(),
    shadow_lg: z.string().optional(),
    shadow_xl: z.string().optional(),
    shadow_2xl: z.string().optional(),
    tracking_normal: z.string().optional(),
    spacing: z.string().optional(),
    sidebar_background: z.string().optional(),
    sidebar: z.string().optional(),
    sidebar_foreground: z.string().optional(),
    sidebar_primary: z.string().optional(),
    sidebar_primary_foreground: z.string().optional(),
    sidebar_accent: z.string().optional(),
    sidebar_accent_foreground: z.string().optional(),
    sidebar_border: z.string().optional(),
    sidebar_ring: z.string().optional(),
    // Dark overrides
    dark_card: z.string().optional(),
    dark_card_foreground: z.string().optional(),
    dark_popover: z.string().optional(),
    dark_popover_foreground: z.string().optional(),
    dark_muted: z.string().optional(),
    dark_muted_foreground: z.string().optional(),
    dark_destructive: z.string().optional(),
    dark_destructive_foreground: z.string().optional(),
    dark_primary: z.string().optional(),
    dark_primary_foreground: z.string().optional(),
    dark_secondary: z.string().optional(),
    dark_secondary_foreground: z.string().optional(),
    dark_accent: z.string().optional(),
    dark_accent_foreground: z.string().optional(),
    dark_background: z.string().optional(),
    dark_foreground: z.string().optional(),
    dark_border: z.string().optional(),
    dark_ring: z.string().optional(),
    dark_input: z.string().optional(),
    dark_chart_1: z.string().optional(),
    dark_chart_2: z.string().optional(),
    dark_chart_3: z.string().optional(),
    dark_chart_4: z.string().optional(),
    dark_chart_5: z.string().optional(),
    dark_sidebar_background: z.string().optional(),
    dark_sidebar: z.string().optional(),
    dark_sidebar_foreground: z.string().optional(),
    dark_sidebar_primary: z.string().optional(),
    dark_sidebar_primary_foreground: z.string().optional(),
    dark_sidebar_accent: z.string().optional(),
    dark_sidebar_accent_foreground: z.string().optional(),
    dark_sidebar_border: z.string().optional(),
    dark_sidebar_ring: z.string().optional(),
    // Custom CSS - allows injecting raw CSS for advanced customization
    custom_css: z.string().max(50000).optional(),
  })
  .partial();

export const notificationFlowSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  event: z.enum(systemNotificationEvents),
  enabled: z.boolean(),
  channels: z.object({
    email: z
      .object({
        enabled: z.boolean(),
        recipients: z.array(z.string().email()),
        subject: z.string().optional(),
        template: z.string().optional(),
      })
      .optional(),
    webhook: z
      .object({
        enabled: z.boolean(),
      })
      .optional(),
  }),
});

export const notificationSettingsSchema = z
  .object({
    email_enabled: z.boolean(),
    email_provider: z.enum(["smtp", "resend"]),
    email_config: z.object({
      smtp_host: z.string().optional(),
      smtp_port: z.number().optional(),
      smtp_secure: z.boolean().optional(),
      smtp_user: z.string().optional(),
      smtp_password: z.string().optional(),
      resend_api_key: z.string().optional(),
      from_email: z.string().optional(),
      from_name: z.string().optional(),
    }),
    webhook_enabled: z.boolean(),
    webhook_config: z.object({
      url: z.string().optional(),
      secret: z.string().optional(),
      headers: z.record(z.string(), z.string()).optional(),
    }),
    flows: z.array(notificationFlowSchema),
  })
  .superRefine((data, ctx) => {
    // If email is enabled, from_email must be provided and valid
    if (data.email_enabled) {
      const fromEmail = data.email_config.from_email;
      if (!fromEmail || fromEmail.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "From email is required when email notifications are enabled",
          path: ["email_config", "from_email"],
        });
      } else if (!z.string().email().safeParse(fromEmail).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid email format",
          path: ["email_config", "from_email"],
        });
      }
    }

    // If webhook is enabled, webhook URL must be provided and valid
    if (data.webhook_enabled) {
      const webhookUrl = data.webhook_config.url;
      if (!webhookUrl || webhookUrl.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Webhook URL is required when webhook notifications are enabled",
          path: ["webhook_config", "url"],
        });
      } else if (!z.string().url().safeParse(webhookUrl).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid URL format",
          path: ["webhook_config", "url"],
        });
      }
    }
  });

// SMTP Form Schema (flattened for frontend form)
export const smtpFormSchema = z.object({
  smtp_enabled: z.boolean(),
  smtp_host: z.string().optional(),
  smtp_port: z.number().min(1).max(65535).optional(),
  smtp_secure: z.boolean().optional(),
  smtp_user: z.string().optional(),
  smtp_password: z.string().optional(),
  from_email: z.string().email("Invalid email address").optional().or(z.literal("")),
  from_name: z.string().optional(),
});

// Personalization Settings
export const personalizationSettingsSchema = z
  .object({
    aiName: z.string().optional(),
    personalityPreset: z
      .enum([
        "professional",
        "friendly",
        "technical",
        "concise",
        "educational",
        "creative",
      ])
      .optional(),
    customGreeting: z.string().optional(),
    primaryLanguage: z
      .enum(["en", "fr", "de", "zh-tw", "es", "it", "ja", "ko"])
      .optional(),
  })
  .partial();

// Auth Provider Settings
export const authProviderSettingsSchema = z
  .object({})
  .merge(availableAuthProvidersValidator)
  .merge(microsoftAuthProviderValidator)
  .merge(googleAuthProviderValidator)
  .refine(
    (data) => {
      if (data.AUTH_PROVIDER === "microsoft") {
        return (
          data.MICROSOFT_CLIENT_ID &&
          data.MICROSOFT_CLIENT_SECRET &&
          data.MICROSOFT_TENANT_ID
        );
      }
      if (data.AUTH_PROVIDER === "google") {
        return data.GOOGLE_CLIENT_ID && data.GOOGLE_CLIENT_SECRET;
      }
      return true;
    },
    {
      message: "Authentication provider requires all necessary credentials",
    }
  );

// Provider schemas
export const providerSchema = z.object({
  id: z.string().uuid(),
  provider: z.string(),
  display_name: z.string(),
  enabled: z.boolean(),
  config: z.any(), // JSONB config
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
});

// Updated Settings Schema for multi-provider structure
export const settingsSchema = z.object({
  base_url: z.string().url().optional(),
  setup_completed: z.boolean().optional(),
  setup_completed_at: z.coerce.date().optional(),
  branding_settings: brandingSettingsSchema.optional(),
  notification_settings: notificationSettingsSchema.optional(),
  personalization: personalizationSettingsSchema.optional(),
});

export const updateSettingsSchema = settingsSchema.partial();

// Legacy provider schema for backward compatibility with non-migrated providers
export const legacyProviderSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  enabled: z.boolean(),
  config: z.any(), // JSONB config
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
});

// Schema for settings with multiple enabled providers
export const findSettingsSchema = z.object({
  id: z.string().uuid(),
  base_url: z.string().nullable(),
  branding_settings: brandingSettingsSchema.optional(),
  notification_settings: notificationSettingsSchema.nullable().optional(),
  personalization: personalizationSettingsSchema.nullable().optional(),
  setup_completed: z.boolean(),
  setup_completed_at: z.coerce.date().nullable(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
  enabledChatProviders: z.array(providerSchema).optional(),
  enabledEmbeddingProviders: z.array(providerSchema).optional(),
  enabledVectorStoreProviders: z.array(providerSchema).optional(),
  enabledAuthProviders: z.array(providerSchema).optional(),
});

// Legacy schemas for backward compatibility (can be removed after migration)
export const legacySettingsSchema = z.object({
  chat_settings: chatSettingsSchema.optional(),
  embedding_settings: embeddingSettingsSchema.optional(),
  vector_store_settings: vectorStoreSettingsSchema.optional(),
  auth_provider_settings: authProviderSettingsSchema.optional(),
  branding_settings: brandingSettingsSchema.optional(),
  base_url: z.string().url().optional(),
  setup_completed: z.boolean().optional(),
  setup_completed_at: z.coerce.date().optional(),
});

export const legacyFindSettingsSchema = z.object({
  id: z.string().uuid(),
  chat_settings: chatSettingsSchema.nullable(),
  embedding_settings: embeddingSettingsSchema.nullable(),
  vector_store_settings: vectorStoreSettingsSchema.nullable(),
  auth_provider_settings: authProviderSettingsSchema.nullable(),
  branding_settings: brandingSettingsSchema.nullable(),
  base_url: z.string().nullable(),
  setup_completed: z.boolean().nullable(),
  setup_completed_at: z.coerce.date().nullable(),
  created_at: z.coerce.date(),
  updated_at: z.coerce.date(),
});

export const settingsValidator = {
  settings: settingsSchema,
  updateSettings: updateSettingsSchema,
  findSettings: findSettingsSchema,
  chatSettings: chatSettingsSchema,
  embeddingSettings: embeddingSettingsSchema,
  vectorStoreSettings: vectorStoreSettingsSchema,
  authProviderSettings: authProviderSettingsSchema,
  provider: providerSchema,
  // Legacy validators for backward compatibility
  legacySettings: legacySettingsSchema,
  legacyFindSettings: legacyFindSettingsSchema,
  brandingSettings: brandingSettingsSchema,
  notificationSettings: notificationSettingsSchema,
  notificationFlow: notificationFlowSchema,
  personalizationSettings: personalizationSettingsSchema,
  smtpForm: smtpFormSchema,
};

export type Settings = z.infer<typeof settingsSchema>;
export type UpdateSettings = z.infer<typeof updateSettingsSchema>;
export type FindSettings = z.infer<typeof findSettingsSchema>;
export type Provider = z.infer<typeof providerSchema>;
export type ChatSettings = z.infer<typeof chatSettingsSchema>;
export type EmbeddingSettings = z.infer<typeof embeddingSettingsSchema>;
export type VectorStoreSettings = z.infer<typeof vectorStoreSettingsSchema>;
export type AuthProviderSettings = z.infer<typeof authProviderSettingsSchema>;

// Legacy types for backward compatibility
export type LegacySettings = z.infer<typeof legacySettingsSchema>;
export type LegacyFindSettings = z.infer<typeof legacyFindSettingsSchema>;
export type BrandingSettings = z.infer<typeof brandingSettingsSchema>;
export type NotificationSettings = z.infer<typeof notificationSettingsSchema>;
export type NotificationFlow = z.infer<typeof notificationFlowSchema>;
export type PersonalizationSettings = z.infer<
  typeof personalizationSettingsSchema
>;
export type SmtpFormValues = z.infer<typeof smtpFormSchema>;
